import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Home } from 'lucide-react';

/**
 * Section home mapping — maps page paths to their module root.
 */
const SECTION_HOMES = {
  // Supply Chain
  '/dashboard/uk-warehouse': { label: 'Supply Chain', path: '/dashboard/master' },
  '/dashboard/factory-orders': { label: 'Supply Chain', path: '/dashboard/master' },
  '/dashboard/cost-dashboard': { label: 'Supply Chain', path: '/dashboard/master' },
  '/dashboard/uk-distributors': { label: 'Supply Chain', path: '/dashboard/master' },
  '/dashboard/uk-orders': { label: 'Supply Chain', path: '/dashboard/master' },
  '/dashboard/uk-invoices': { label: 'Supply Chain', path: '/dashboard/master' },
  '/dashboard/quotes': { label: 'Supply Chain', path: '/dashboard/master' },
  '/dashboard/onboarding': { label: 'Supply Chain', path: '/dashboard/master' },
  '/dashboard/stock-transfers': { label: 'Supply Chain', path: '/dashboard/master' },
  '/dashboard/uk-salons': { label: 'Supply Chain', path: '/dashboard/master' },
  // Stock
  '/dashboard/stock': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/stock/reconciliation': { label: 'Stock', path: '/dashboard/stock' },
  '/dashboard/stock/scanner': { label: 'Stock', path: '/dashboard/stock' },
  '/dashboard/barcode': { label: 'Stock', path: '/dashboard/stock' },
  '/dashboard/stock-setup': { label: 'Stock', path: '/dashboard/stock' },
  // Master / CEO
  '/dashboard/master': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/ceo-command': { label: 'Dashboard', path: '/dashboard/master' },
  '/dashboard/ceo-digest': { label: 'Dashboard', path: '/dashboard/master' },
  // Sales & Analytics
  '/dashboard/sales-analytics': { label: 'Dashboard', path: '/dashboard/master' },
  '/dashboard/sales-funnel': { label: 'Dashboard', path: '/dashboard/master' },
  '/dashboard/leads': { label: 'Dashboard', path: '/dashboard/master' },
  '/dashboard/territory-performance': { label: 'Dashboard', path: '/dashboard/master' },
  '/dashboard/leaderboard': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/revenue': { label: 'Dashboard', path: '/dashboard' },
  // Salon Management
  '/dashboard/salon-pipeline': { label: 'Dashboard', path: '/dashboard/master' },
  '/dashboard/salon-registry': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/salon-settings': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/salon-performance': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/my-salon-orders': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/my-salon-invoices': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/my-salon-quotes': { label: 'Dashboard', path: '/dashboard' },
  // Delivery
  '/dashboard/delivery': { label: 'Dashboard', path: '/dashboard/master' },
  // Automation & WhatsApp
  '/dashboard/automation': { label: 'Dashboard', path: '/dashboard/master' },
  '/dashboard/whatsapp-automation': { label: 'Dashboard', path: '/dashboard/master' },
  '/dashboard/whatsapp': { label: 'Dashboard', path: '/dashboard' },
  // Education & Training
  '/dashboard/education': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/training': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/protocols': { label: 'Dashboard', path: '/dashboard' },
  // Products & Catalogue
  '/dashboard/catalogue': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/cart': { label: 'Catalogue', path: '/dashboard/catalogue' },
  '/dashboard/starter-packs': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/calculator': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/reorder': { label: 'Dashboard', path: '/dashboard' },
  // People & Team
  '/dashboard/staff': { label: 'Dashboard', path: '/dashboard/master' },
  '/dashboard/payroll': { label: 'Dashboard', path: '/dashboard/master' },
  '/dashboard/my-payroll': { label: 'Dashboard', path: '/dashboard' },
  // Content & Media
  '/dashboard/gallery': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/lookbook': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/bulletins': { label: 'Dashboard', path: '/dashboard/master' },
  '/dashboard/editor/homepage': { label: 'Dashboard', path: '/dashboard/master' },
  // Admin & Settings
  '/dashboard/feature-toggles': { label: 'Dashboard', path: '/dashboard/master' },
  '/dashboard/moderation': { label: 'Dashboard', path: '/dashboard/master' },
  '/dashboard/security': { label: 'Dashboard', path: '/dashboard' },
  // Other
  '/dashboard/demo-invites': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/start': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/crm': { label: 'Dashboard', path: '/dashboard/master' },
  '/dashboard/forecast': { label: 'Dashboard', path: '/dashboard/master' },
  '/dashboard/warehouse-reorder': { label: 'Supply Chain', path: '/dashboard/master' },
  '/dashboard/messages': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/verification': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/visits': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/rewards': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/about': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/pro-status': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/business-planner': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/share-pack': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/rep-alerts': { label: 'Dashboard', path: '/dashboard/master' },
  '/dashboard/inventory-intelligence': { label: 'Dashboard', path: '/dashboard/master' },
  '/dashboard/weekly-digest': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/faq': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/help': { label: 'Dashboard', path: '/dashboard' },
  '/dashboard/profile': { label: 'Dashboard', path: '/dashboard' },
};

/**
 * Smart back: uses browser history if available, else falls back to section home.
 */
/* back_button_28oct2026 (CEO, found on live): referrer is unreliable in a
   SPA, so back often dumped users on an empty range page. React Router's
   history index is the truth: real in-app history -> navigate(-1) with
   scroll restored; otherwise the catalogue/section fallback. */
function canGoBackNow() {
  const st = window.history.state;
  if (st && typeof st.idx === 'number') return st.idx > 0;
  return window.history.length > 2 && !!document.referrer
    && document.referrer.includes(window.location.host);
}

export function useSmartBack(fallback = '/dashboard') {
  const navigate = useNavigate();
  return () => {
    if (canGoBackNow()) {
      navigate(-1);
    } else {
      navigate(fallback);
    }
  };
}

/**
 * Resolve section home for dynamic routes like /dashboard/catalogue/:id
 */
function resolveSectionHome(path) {
  // Exact match first
  if (SECTION_HOMES[path]) return SECTION_HOMES[path];
  // Dynamic route patterns
  if (path.startsWith('/dashboard/catalogue/')) return { label: 'Products', path: '/dashboard/catalogue' };
  if (path.startsWith('/dashboard/crm/')) return { label: 'CRM', path: '/dashboard/crm' };
  if (path.startsWith('/dashboard/salon-search/')) return { label: 'Salon Search', path: '/dashboard/salon-search' };
  if (path.startsWith('/dashboard/uk-salons/')) return { label: 'UK Salons', path: '/dashboard/uk-salons' };
  if (path.startsWith('/dashboard/results-logbook/')) return { label: 'Lookbook', path: '/dashboard/lookbook' };
  if (path.startsWith('/dashboard/staff/')) return { label: 'Staff', path: '/dashboard/staff' };
  if (path.startsWith('/dashboard/orders/')) return { label: 'Orders', path: '/dashboard/orders' };
  if (path.startsWith('/dashboard/education/')) return { label: 'Training', path: '/dashboard/education' };
  return { label: 'Dashboard', path: '/dashboard' };
}

/**
 * Navigation bar with Back + Section Home buttons.
 * Back = previous page (history-based with safe fallback).
 * Section Home = module root for the current area.
 */
export default function BackButton({ fallback, sectionHome, sectionLabel, label = 'Back', className = '', light = false }) {
  const navigate = useNavigate();
  const currentPath = window.location.pathname;

  // Determine section home from mapping or props
  const section = sectionHome
    ? { label: sectionLabel || 'Home', path: sectionHome }
    : resolveSectionHome(currentPath);

  const effectiveFallback = fallback || section.path;

  const goBack = () => {
    if (canGoBackNow()) {
      // Scroll position is auto-restored by the browser on navigate(-1)
      navigate(-1);
    } else {
      navigate(effectiveFallback);
    }
  };

  const goSectionHome = () => {
    navigate(section.path);
  };

  // Don't show section home if we're already at the section root
  const showSectionHome = section.path !== currentPath;

  return (
    <div className={`flex items-center gap-3 mb-3 ${className}`} data-testid="nav-bar">
      {/* tap_target_44_07oct2026 (Rule #11): min 44x44 tappable, negative
          margin keeps the visual rhythm unchanged */}
      <button
        onClick={goBack}
        className={`flex items-center gap-1 min-h-[44px] min-w-[44px] px-2 -mx-2 -my-2 transition-colors group text-sm ${light ? 'text-[#E9DDE4] hover:text-white' : 'text-[#6B7280] hover:text-[#2B1F27]'}`}
        data-testid="back-button"
      >
        <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
        <span>{label}</span>
      </button>
      {showSectionHome && (
        <>
          <span className="text-[#C8D1DB]">|</span>
          <button
            onClick={goSectionHome}
            className={`flex items-center gap-1 min-h-[44px] min-w-[44px] px-2 -mx-2 -my-2 transition-colors text-sm ${light ? 'text-[#E9DDE4] hover:text-[#D9BE8E]' : 'text-[#6B7280] hover:text-[#B99A65]'}`}
            data-testid="section-home-button"
          >
            <Home className="w-3.5 h-3.5" />
            <span>{section.label}</span>
          </button>
        </>
      )}
    </div>
  );
}
